import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  MoreVertical,
  Upload,
  Loader2,
} from 'lucide-react';
import { SettlementStatus, VendorType, Vendor } from '../../models/Accountant';
import {
  getVendorsWithSettlements,
  settleVendorAmount,
  mapVendorSettlementToVendor,
  VendorSettlementResponse,
} from '../../services/MasterAdmin/accountantService';
import { formatAccountantCurrency } from '../../utils/accountantOfferBreakdown';
import {
  AccountantInfoTooltip,
  PLATFORM_OFFER_POLICY_TOOLTIP,
} from '../../components/Accountant/AccountantInfoTooltip';
import {
  DoctorClinicLabel,
  isDoctorVendorType,
} from '../../components/Accountant/DoctorClinicLabel';

const VendorSettlementsPage: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SettlementStatus | 'ALL'>('ALL');
  const [vendorTypeFilter, setVendorTypeFilter] = useState<VendorType | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVendors, setTotalVendors] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [settlingVendorId, setSettlingVendorId] = useState<string | null>(null);
  const [showSettleDialog, setShowSettleDialog] = useState(false);
  const [selectedVendorForSettlement, setSelectedVendorForSettlement] = useState<Vendor | null>(null);
  const [settlementSuccess, setSettlementSuccess] = useState<any>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const filterMenuRef = useRef<HTMLDivElement | null>(null);

  // Fetch vendors from API
  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const statusParam = statusFilter === 'ALL' ? undefined : statusFilter.toLowerCase();
      const vendorTypeParam = vendorTypeFilter === 'ALL' ? undefined : vendorTypeFilter;
      const searchParam = searchTerm.trim() || undefined;

      const response = await getVendorsWithSettlements({
        status: statusParam as any,
        vendorType: vendorTypeParam,
        search: searchParam,
        page: currentPage,
        limit: 50,
      });

      const mappedVendors = response.data
        .filter((vendor: any) => {
          if (String(vendor?.vendorType || '').toUpperCase() !== 'DOCTOR') return true;
          const lifecycle = String(vendor?.lifecycleStatus || '').toUpperCase();
          return lifecycle === '' || lifecycle === 'COMPLETED';
        })
        .map(mapVendorSettlementToVendor);
      setVendors(mappedVendors);
      setTotalPages(response.pagination.totalPages);
      setTotalVendors(response.pagination.total);
    } catch (err: any) {
      console.error('Error fetching vendors:', err);
      setError(err.response?.data?.message || 'Failed to fetch vendors. Please try again.');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, vendorTypeFilter, searchTerm]);

  // Fetch vendors when filters or page change
  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (searchTerm && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, currentPage]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadge = (status: SettlementStatus) => {
    const statusConfig = {
      PENDING: {
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        icon: Clock,
      },
      PROCESSING: {
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: Clock,
      },
      SETTLED: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        icon: CheckCircle2,
      },
      ON_HOLD: {
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
        icon: AlertCircle,
      },
      FAILED: {
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        icon: XCircle,
      },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  // Client-side filtering is now handled by API, but we keep this for any additional filtering
  const filteredVendors = vendors;

  const handleSettleClick = (vendor: Vendor, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setSelectedVendorForSettlement(vendor);
    setSettlementSuccess(null);
    setShowSettleDialog(true);
  };

  const handleSettleConfirm = async () => {
    if (!selectedVendorForSettlement) return;

    try {
      setSettlingVendorId(selectedVendorForSettlement.id);
      const response = await settleVendorAmount(selectedVendorForSettlement.id);
      setSettlementSuccess(response.data);
      // Refresh the vendor list
      fetchVendors();
    } catch (error: any) {
      console.error('Error settling vendor:', error);
      setSettlementSuccess({
        error: true,
        message: error.response?.data?.message || 'Failed to process settlement. Please try again.',
      });
    } finally {
      setSettlingVendorId(null);
    }
  };

  const handleCloseSettleDialog = () => {
    setShowSettleDialog(false);
    setSelectedVendorForSettlement(null);
    setSettlementSuccess(null);
  };

  const handleViewDetails = (vendorId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setOpenMenuId(null);
    navigate(`/accountant/settlements/${vendorId}`);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterMenu && filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterMenu]);

  return (
    <div className="space-y-6">
        {/* Header with Search and Actions */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Search and Filters */}
          <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className={`px-4 py-2 border rounded-lg transition-colors flex items-center space-x-2 ${
                  showFilterMenu || statusFilter !== 'ALL' || vendorTypeFilter !== 'ALL'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
                {(statusFilter !== 'ALL' || vendorTypeFilter !== 'ALL') && (
                  <span className="ml-1 px-1.5 py-0.5 bg-primary-600 text-white text-xs rounded-full">
                    {[statusFilter !== 'ALL' ? 1 : 0, vendorTypeFilter !== 'ALL' ? 1 : 0].reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </button>
              
              {showFilterMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value as SettlementStatus | 'ALL');
                          setCurrentPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="ALL">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="SETTLED">Settled</option>
                        <option value="ON_HOLD">On Hold</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vendor Type
                      </label>
                      <select
                        value={vendorTypeFilter}
                        onChange={(e) => {
                          setVendorTypeFilter(e.target.value as VendorType | 'ALL');
                          setCurrentPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="ALL">All Types</option>
                        <option value="BLOOD_BANK">Blood Bank</option>
                        <option value="MEDICAL_STORE">Medical Store</option>
                        <option value="AMBULANCE">Ambulance</option>
                        <option value="HOSPITAL">Hospital</option>
                        <option value="DOCTOR">Doctor</option>
                        <option value="LAB">Lab</option>
                        <option value="PRODUCT">Product</option>
                        <option value="DELIVERY_PARTNER">Delivery Partner</option>
                      </select>
                    </div>
                    <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => {
                          setStatusFilter('ALL');
                          setVendorTypeFilter('ALL');
                          setCurrentPage(1);
                        }}
                        className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => setShowFilterMenu(false)}
                        className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <button className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button className="rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700">
              Batch Settlement
            </button>
          </div>
        </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Vendors</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {loading ? '...' : totalVendors}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Pending Settlements</p>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
            {loading ? '...' : vendors.filter((v) => v.settlementStatus === 'PENDING').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Pending Amount</p>
          {(() => {
            const totalPayable = loading
              ? 0
              : vendors
                  .filter((v) => v.settlementStatus === 'PENDING' || v.settlementStatus === 'ON_HOLD')
                  .reduce((sum, v) => sum + ((v as any).pendingAmount || 0), 0);
            return (
              <p className={`text-2xl font-bold mt-1 ${
                totalPayable < 0 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-gray-900 dark:text-white'
              }`}>
                {loading ? '...' : formatCurrency(totalPayable)}
                {totalPayable < 0 && (
                  <span className="ml-2 text-sm text-red-500 dark:text-red-400">
                    (Vendors owe)
                  </span>
                )}
              </p>
            );
          })()}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Settled</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {loading ? '...' : vendors.filter((v) => v.settlementStatus === 'SETTLED').length}
          </p>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total Earnings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1">
                    Platform Offer
                    <AccountantInfoTooltip
                      content={PLATFORM_OFFER_POLICY_TOOLTIP}
                      label="Platform offer settlement policy"
                      panelClassName="w-80"
                    />
                  </span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1">
                    Commission
                    <AccountantInfoTooltip
                      content={
                        <>
                          Platform commission after booking offers. When an offer applies, gross commission
                          is shown below the net amount.
                        </>
                      }
                      label="Commission column help"
                    />
                  </span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Net Payable
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total Pending Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredVendors.map((vendor) => (
                <tr
                  key={vendor.id}
                  onClick={() => handleViewDetails(vendor.id)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isDoctorVendorType(vendor.vendorType) ? (
                      <DoctorClinicLabel
                        clinicName={vendor.clinicName}
                        doctorName={vendor.doctorName}
                        fallbackName={vendor.name}
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {vendor.name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {vendor.vendorType.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(vendor.totalEarnings)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Customer paid</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {((vendor.totalOfferDiscount ?? 0) > 0) ? (
                      <div className="text-sm">
                        <div className="font-semibold text-emerald-700 dark:text-emerald-400">
                          −{formatCurrency(vendor.totalOfferDiscount ?? 0)}
                        </div>
                        {(vendor.totalOfferAbsorbed ?? 0) > 0 && (
                          <div className="text-xs text-amber-700 dark:text-amber-400">
                            Platform absorbed {formatAccountantCurrency(vendor.totalOfferAbsorbed ?? 0)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {String(vendor.vendorType) === 'DELIVERY_PARTNER' ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                        NA
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900 dark:text-white">
                        <div className="font-medium">{formatCurrency(vendor.platformCommission)}</div>
                        {((vendor.totalBaseCommission ?? 0) > vendor.platformCommission + 0.009) && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Gross comm.: {formatCurrency(vendor.totalBaseCommission ?? 0)}
                          </div>
                        )}
                        {vendor.gst !== undefined && vendor.gst > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            GST: {formatCurrency(vendor.gst)}
                          </div>
                        )}
                        {vendor.commissionRate !== undefined && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {vendor.commissionRate.toFixed(2)}%
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(vendor.netPayable)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Vendor payout</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-semibold ${
                      (vendor as any).pendingAmount < 0 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {formatCurrency((vendor as any).pendingAmount || 0)}
                      {(vendor as any).pendingAmount < 0 && (
                        <span className="ml-2 text-xs text-red-500 dark:text-red-400">
                          (Vendor owes)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(vendor.settlementStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === vendor.id ? null : vendor.id);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="More options"
                        disabled={settlingVendorId === vendor.id}
                      >
                        {settlingVendorId === vendor.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <MoreVertical className="w-4 h-4" />
                        )}
                      </button>
                      
                      {openMenuId === vendor.id && (
                        <div
                          ref={(el) => {
                            menuRefs.current[vendor.id] = el;
                          }}
                          className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                        >
                          <div className="py-1">
                            <button
                              onClick={(e) => handleViewDetails(vendor.id, e)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                            {(vendor.settlementStatus === 'PENDING' || vendor.settlementStatus === 'ON_HOLD') && (
                              (vendor as any).canSettle !== false && ((vendor as any).pendingAmount ?? 0) > 0 ? (
                              <button
                                onClick={(e) => handleSettleClick(vendor, e)}
                                className="w-full text-left px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                disabled={settlingVendorId === vendor.id}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Settle</span>
                              </button>
                              ) : (
                              <div
                                className="w-full px-4 py-2 text-sm text-gray-400 dark:text-gray-500 flex items-center space-x-2 cursor-not-allowed"
                                title="Cannot settle — net payable is zero or negative (vendor owes platform)"
                              >
                                <XCircle className="w-4 h-4" />
                                <span>Settle unavailable</span>
                              </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600 dark:text-primary-400" />
          <p className="text-gray-500 dark:text-gray-400 mt-4">Loading vendors...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={fetchVendors}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredVendors.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">No vendors found matching your filters.</p>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing page {currentPage} of {totalPages} ({totalVendors} total vendors)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Settlement Confirmation Dialog */}
      {showSettleDialog && selectedVendorForSettlement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => {
                setShowSettleDialog(false);
                setSelectedVendorForSettlement(null);
              }}
            ></div>

            {/* Dialog panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              {/* Header */}
              <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirm Settlement
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Review settlement details before processing
                </p>
              </div>

              {/* Content */}
              <div className="bg-white dark:bg-gray-800 px-6 py-4">
                {settlementSuccess && !settlementSuccess.error ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-lg font-medium text-green-800 dark:text-green-300 mb-2">
                            Settlement Processed Successfully!
                          </h4>
                          <div className="space-y-2 text-sm text-green-700 dark:text-green-400">
                            <p>
                              <span className="font-medium">Vendor:</span> {settlementSuccess.vendorName}
                            </p>
                            <p>
                              <span className="font-medium">Amount Settled:</span> {formatCurrency(settlementSuccess.totalAmount)}
                            </p>
                            <p>
                              <span className="font-medium">Entries Processed:</span> {settlementSuccess.entryCount}
                            </p>
                            <p>
                              <span className="font-medium">Settlement Reference:</span> {settlementSuccess.settlementRef}
                            </p>
                            <p>
                              <span className="font-medium">Settled At:</span> {new Date(settlementSuccess.settledAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : settlementSuccess && settlementSuccess.error ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">
                          Settlement Failed
                        </h4>
                        <p className="text-sm text-red-700 dark:text-red-400">
                          {settlementSuccess.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {((selectedVendorForSettlement as any).pendingAmount ?? 0) <= 0 && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          Settlement is not available — net payable is zero or negative. The vendor owes the platform and this amount cannot be settled as a payout.
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Vendor Details</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">Name:</span> {selectedVendorForSettlement.name}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">ID:</span> {selectedVendorForSettlement.id}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">Type:</span> {selectedVendorForSettlement.vendorType.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">Status:</span> {selectedVendorForSettlement.settlementStatus}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Settlement Amount</p>
                      <p className={`text-2xl font-bold mt-2 ${
                        (selectedVendorForSettlement as any).pendingAmount < 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {formatCurrency((selectedVendorForSettlement as any).pendingAmount || 0)}
                      </p>
                      {(selectedVendorForSettlement as any).pendingAmount < 0 && (
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                          (Vendor owes this amount)
                        </p>
                      )}
                    </div>

                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                      <p className="text-sm text-yellow-800 dark:text-yellow-300">
                        This will settle only T+2 eligible entries (completed at least 2 days ago). For individual selection, open the vendor ledger details page.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                {settlementSuccess && !settlementSuccess.error ? (
                  <button
                    onClick={handleCloseSettleDialog}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Close</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCloseSettleDialog}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                      disabled={settlingVendorId === selectedVendorForSettlement.id}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSettleConfirm}
                      disabled={
                        settlingVendorId === selectedVendorForSettlement.id ||
                        ((selectedVendorForSettlement as any).pendingAmount ?? 0) <= 0
                      }
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {settlingVendorId === selectedVendorForSettlement.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Confirm Settlement</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorSettlementsPage;
